import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { usePaulAccess } from '@/lib/paulAccess';
import {
  listCollections, createCollection, deleteCollection, listItems, removeItem, listHistory,
  type UserCollection, type SavedItem,
} from '@/lib/collections';

const fmtDate = (key = ''): string => {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (d) return new Date(Date.UTC(+d[1], +d[2] - 1, +d[3])).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, 1)).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  return key || '—';
};
const watchUrl = (it: SavedItem): string =>
  it.sourceTimestampUrl || (it.videoId ? `https://www.youtube.com/watch?v=${it.videoId}${it.sourceTimestampSec != null ? `&t=${Math.floor(it.sourceTimestampSec)}s` : ''}` : '#');

function SnippetCard({ it, onRemove }: { it: SavedItem; onRemove?: () => void }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
      <div className="min-w-0">
        <div className="font-medium">{it.title || '(untitled)'}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-white/45">
          {it.channelTitle ? <span>{it.channelTitle}</span> : null}
          {it.eventDateKey ? <span>· {fmtDate(it.eventDateKey)}</span> : null}
        </div>
        {it.videoId ? <a href={watchUrl(it)} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-xs text-amber-300/80 hover:text-amber-200">▶ Watch source</a> : null}
      </div>
      {onRemove ? <button type="button" onClick={onRemove} title="Remove" className="shrink-0 rounded px-1.5 text-white/40 hover:bg-white/10 hover:text-white">✕</button> : null}
    </div>
  );
}

export default function Collections() {
  const { email, isReady } = usePaulAccess();
  const [cols, setCols] = useState<UserCollection[]>([]);
  const [items, setItems] = useState<Record<string, SavedItem[]>>({});
  const [history, setHistory] = useState<SavedItem[]>([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    const c = await listCollections();
    setCols(c);
    const map: Record<string, SavedItem[]> = {};
    await Promise.all(c.map(async (col) => { map[col.id] = await listItems(col.id); }));
    setItems(map);
    setHistory(await listHistory(60));
    setLoading(false);
  }, []);

  useEffect(() => { if (isReady && email) void reload(); else if (isReady) setLoading(false); }, [isReady, email, reload]);

  const create = async () => {
    const name = newName.trim(); if (!name) return;
    await createCollection(name); setNewName(''); await reload();
  };
  const removeCol = async (id: string) => { await deleteCollection(id); await reload(); };
  const removeSnip = async (id: string) => { await removeItem(id); await reload(); };

  return (
    <div className="min-h-screen bg-[#050416] text-white">
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-10">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/dashboard/thirteen" className="rounded-md border border-white/15 px-3 py-1.5 text-sm hover:bg-white/10">← Calendar</Link>
          <h1 className="text-2xl font-semibold tracking-tight">My Collections</h1>
        </div>

        {isReady && !email ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-center text-sm text-white/60">
            <p>Sign in on the calendar to save video snippets into collections and see your history.</p>
            <Link to="/dashboard/thirteen" className="mt-3 inline-block rounded-md border border-amber-400/50 bg-amber-500/20 px-3 py-1.5 font-semibold text-amber-100 hover:bg-amber-500/30">Go to calendar</Link>
          </div>
        ) : loading ? (
          <div className="text-sm text-white/50">Loading…</div>
        ) : (
          <div className="space-y-8">
            <div className="flex gap-2">
              <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void create(); }} placeholder="New collection name…" className="flex-1 rounded-md border border-white/15 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-amber-400/50" />
              <button type="button" onClick={create} className="rounded-md border border-amber-400/50 bg-amber-500/20 px-3 py-2 text-sm font-semibold text-amber-100 hover:bg-amber-500/30">Create</button>
            </div>

            {cols.length === 0 ? <div className="text-sm text-white/50">No collections yet — save events from the calendar, or create one above.</div> : null}

            {cols.map((col) => (
              <section key={col.id}>
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-white/70">{col.kind === 'queue' ? '▸ Queue' : col.name} <span className="ml-1 text-white/35">({(items[col.id] || []).length})</span></h2>
                  {col.kind !== 'queue' ? <button type="button" onClick={() => void removeCol(col.id)} className="text-xs text-white/40 hover:text-red-300">Delete</button> : null}
                </div>
                {(items[col.id] || []).length === 0 ? (
                  <div className="text-xs text-white/40">Empty.</div>
                ) : (
                  <div className="space-y-1.5">{(items[col.id] || []).map((it) => <SnippetCard key={it.id} it={it} onRemove={() => void removeSnip(it.id)} />)}</div>
                )}
              </section>
            ))}

            <section>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-white/70">History <span className="ml-1 text-white/35">({history.length})</span></h2>
              {history.length === 0 ? <div className="text-xs text-white/40">Nothing watched yet.</div> : (
                <div className="space-y-1.5">{history.map((it) => <SnippetCard key={it.id} it={it} />)}</div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
